import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"

// Periodically refreshes the page using Turbo.visit.
// Stops automatically when the "active" value is false.
// Skips refresh when the browser tab is hidden to avoid wasting requests.
// Preserves scroll position across refreshes via sessionStorage.
//
// Usage:
//   <div data-controller="auto-refresh"
//        data-auto-refresh-interval-value="10000"
//        data-auto-refresh-active-value="true">
//
// first-interval delays only the first refresh, for a block pushed in once the page's data is ready
// (v2/reporting/account_health_index/_index_landed). The later ones keep the slower interval, because a
// refresh that starts while another is still loading cancels it.
//
const SCROLL_KEY = "auto-refresh-scroll-y"

export default class extends Controller {
  static values = {
    interval: { type: Number, default: 10000 },
    firstInterval: { type: Number, default: 0 },
    active: { type: Boolean, default: true }
  }

  connect() {
    const savedY = sessionStorage.getItem(SCROLL_KEY)
    if (savedY != null) {
      sessionStorage.removeItem(SCROLL_KEY)
      const y = parseInt(savedY, 10)
      // Restore after turbo:load, which fires after Turbo's scroll reset
      document.addEventListener("turbo:load", () => {
        window.scrollTo(0, y)
      }, { once: true })
    }

    if (this.activeValue) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.timer = setTimeout(() => {
      this.refresh()
      this.timer = setInterval(() => this.refresh(), this.intervalValue)
    }, this.firstIntervalValue || this.intervalValue)
  }

  refresh() {
    if (!document.hidden) {
      sessionStorage.setItem(SCROLL_KEY, window.scrollY.toString())
      visit(window.location.href, { action: "replace" })
    }
  }

  stopPolling() {
    if (this.timer) {
      clearTimeout(this.timer)
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
